/**
 * The Venues page's four layouts (BON-538).
 *
 * The rule the breakpoints exist to keep: THE ROSTER NEVER SCROLLS SIDEWAYS
 * WHILE IT COULD INSTEAD HAVE THE PAGE'S FULL WIDTH. Columns are shed only at
 * the narrow end, and once shed they stay shed — a column that disappeared at
 * 1400 and came back at 1000 because the map moved out of its way would read as
 * a bug, whichever way you met it.
 *
 * So, widest to narrowest:
 *
 *   split    map beside the roster, every column
 *   stacked  map above the roster, every column — the roster has the full width
 *   compact  map above the roster, "Partner since" dropped
 *   phone    no map, the roster becomes a stack of cards
 *
 * The thresholds are COMPUTED from the columns' own minimum widths and the
 * chrome around them, not chosen. Picking numbers means they quietly stop
 * matching the table the first time a column's minimum changes.
 *
 * They assume the nav panel is EXPANDED, which is the worst case; with it
 * collapsed to the icon rail every layout simply gets 224px more than it needs.
 */
import { useMediaQuery } from '../../../../hooks/useViewport';

export type VenuesLayout = 'phone' | 'compact' | 'stacked' | 'split';

/** What the full column set needs before it starts scrolling. Keep in step with
 *  the minimums in `venue-columns.tsx`. */
export const ROSTER_FULL_MIN_PX = 816;
/** The same for the set with "Partner since" dropped. */
export const ROSTER_COMPACT_MIN_PX = 720;

/** The expanded nav panel, and `DashboardsPage`'s desktop side padding. */
const NAV_PANEL_PX = 288;
const PAGE_PADDING_PX = 64;
/** The gap between the two columns of the split layout. */
const SPLIT_GAP_PX = 18;
/** The roster's share of the split row — `2fr` of `2fr 1fr`. */
const ROSTER_SHARE = 2 / 3;

/** Above this the map fits beside a roster that still shows every column. */
export const VENUES_SPLIT_MIN_PX = Math.ceil(
  ROSTER_FULL_MIN_PX / ROSTER_SHARE + SPLIT_GAP_PX + NAV_PANEL_PX + PAGE_PADDING_PX,
);

/** Above this a FULL-WIDTH roster still shows every column. */
export const VENUES_FULL_COLUMNS_MIN_PX = ROSTER_FULL_MIN_PX + NAV_PANEL_PX + PAGE_PADDING_PX;

/** Below this the roster becomes a stack of cards — its columns cannot survive. */
export const VENUES_PHONE_MAX_PX = 767;

export function useVenuesLayout(): VenuesLayout {
  const isPhone = useMediaQuery(`(max-width: ${VENUES_PHONE_MAX_PX}px)`);
  const fitsSplit = useMediaQuery(`(min-width: ${VENUES_SPLIT_MIN_PX}px)`);
  const fitsAllColumns = useMediaQuery(`(min-width: ${VENUES_FULL_COLUMNS_MIN_PX}px)`);

  if (isPhone) return 'phone';
  if (fitsSplit) return 'split';
  return fitsAllColumns ? 'stacked' : 'compact';
}
